import { PlansFundRoleByLumpsumAction, PlansScreen } from '../../../../../analytics/plansEvents'
import {
  trackPlansLumpsumFundTileOpen,
  trackPlansLumpsumFundTilePrimaryCta
} from '../../../../../analytics/plansTrack'
import { LumpsumRecommendationActionType } from '../../../../api/common.types'
import { usePlanBuyAction } from '../../../../../execution/buy/usePlanBuyAction'
import { usePlanSellAction } from '../../../../../execution/sell/usePlanSellAction'
import type { LumpsumRebalanceSellPlanRecommendationDetails } from '../../../api/rebalanceRecommendation.types'
import { openRecommendationFundDetail } from '../../../open/planRecommendationOpen'
import {
  buildPlanRecommendationFundActionLabel,
  PlanRecommendationFundTile,
  RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS
} from '../../../shared/funds/PlanRecommendationFundTile'
import { RecommendationCounterFundsSection } from '../../../shared/funds/RecommendationCounterFundsSection'
import { RecommendationFundSingleSection } from '../../../shared/funds/RecommendationFundSingleSection'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { RebalancePlanImportantNote } from '../../important-note/RebalancePlanImportantNote'

type RebalanceSellPlanRecommendationHeaderProps = {
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails | null
  entryIsin: string
  selectedBuyIndex: number
  onSelectBuyIndex: (selectedIndex: number) => void
  transactionCta: unknown
}

export function RebalanceSellPlanRecommendationHeader({
  planDetails,
  entryIsin,
  selectedBuyIndex,
  onSelectBuyIndex,
  transactionCta
}: RebalanceSellPlanRecommendationHeaderProps) {
  const { onSell } = usePlanSellAction({
    transactionCta,
    isRecommendationSheetSource: true,
    analyticsScreen: PlansScreen.LumpsumRebalanceRecommendation
  })
  const { onBuy } = usePlanBuyAction({
    transactionCta,
    isRecommendationSheetSource: true,
    analyticsScreen: PlansScreen.LumpsumRebalanceRecommendation
  })
  const sellPlanDetails = planDetails
  const isEntryActionCompleted = sellPlanDetails?.executed === true
  const sellFundId = sellPlanDetails?.isin ?? entryIsin ?? 'sell-current'
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: sellPlanDetails?.totalTransferAmount ?? 0 })
  const fundsToBeBought = sellPlanDetails?.fundsToBeBought ?? []
  const screenName = PlansScreen.LumpsumRebalanceRecommendation

  return (
    <div className="pt-9 pb-4">
      <RecommendationFundSingleSection
        title="Sell"
        type={LumpsumRecommendationActionType.SELL}
        highlightIsin={isEntryActionCompleted ? '' : entryIsin}
        fundId={sellFundId}
      >
        <PlanRecommendationFundTile
          mfLogoUrl={sellPlanDetails?.mfLogoUrl ?? ''}
          fundName={sellPlanDetails?.fundName ?? ''}
          fundNature={sellPlanDetails?.fundNature ?? ''}
          fundCategory={sellPlanDetails?.fundCategory ?? ''}
          powerRating={sellPlanDetails?.powerRating ?? ''}
          powerRank={sellPlanDetails?.powerRank ?? null}
          purchaseMode={sellPlanDetails?.purchaseMode ?? ''}
          fundIsin={sellFundId}
          widthClassName={RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS}
          onCardClick={() => {
            trackPlansLumpsumFundTileOpen({
              screenName,
              fundRole: PlansFundRoleByLumpsumAction[LumpsumRecommendationActionType.SELL],
              fundName: sellPlanDetails?.fundName ?? '',
              fundIsin: sellFundId
            })
            openRecommendationFundDetail(sellFundId)
          }}
          recommendationAction={{
            action: LumpsumRecommendationActionType.SELL,
            recommendationFormattedAmount: sellRecommendationFormattedAmount,
            isCompleted: isEntryActionCompleted,
            onActionClick: () => {
              trackPlansLumpsumFundTilePrimaryCta({
                screenName,
                buttonLabel: buildPlanRecommendationFundActionLabel(
                  LumpsumRecommendationActionType.SELL,
                  sellRecommendationFormattedAmount
                ),
                fundName: sellPlanDetails?.fundName ?? '',
                fundIsin: sellFundId
              })
              void onSell({
                isin: sellFundId,
                fundName: sellPlanDetails?.fundName ?? '',
                amount: sellRecommendationFormattedAmount.value,
                currentValue: sellPlanDetails?.currentValue ?? 0
              })
            }
          }}
        />
      </RecommendationFundSingleSection>
      <RecommendationCounterFundsSection
        funds={fundsToBeBought}
        titleSingle="Buy"
        titleMulti={`Buy ${fundsToBeBought.length} funds`}
        type={LumpsumRecommendationActionType.BUY}
        onSelect={onSelectBuyIndex}
        sectionClassName="pt-8"
        renderFundTile={(fund, widthClassName) => {
          const fundIsin = fund?.isin ?? ''
          const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: fund?.buyAmount ?? 0 })
          return (
            <PlanRecommendationFundTile
              mfLogoUrl={fund?.mfLogoUrl ?? ''}
              fundName={fund?.fundName ?? ''}
              fundNature={fund?.fundNature ?? ''}
              fundCategory={fund?.fundCategory ?? ''}
              powerRating={fund?.powerRating ?? ''}
              powerRank={fund?.powerRank ?? null}
              purchaseMode={fund?.purchaseMode ?? ''}
              fundIsin={fundIsin}
              widthClassName={widthClassName}
              onCardClick={() => {
                trackPlansLumpsumFundTileOpen({
                  screenName,
                  fundRole: PlansFundRoleByLumpsumAction[LumpsumRecommendationActionType.BUY],
                  fundName: fund?.fundName ?? '',
                  fundIsin
                })
                openRecommendationFundDetail(fundIsin)
              }}
              recommendationAction={{
                action: LumpsumRecommendationActionType.BUY,
                recommendationFormattedAmount: buyRecommendationFormattedAmount,
                isCompleted: fund?.executed === true,
                onActionClick: () => {
                  trackPlansLumpsumFundTilePrimaryCta({
                    screenName,
                    buttonLabel: buildPlanRecommendationFundActionLabel(
                      LumpsumRecommendationActionType.BUY,
                      buyRecommendationFormattedAmount
                    ),
                    fundName: fund?.fundName ?? '',
                    fundIsin
                  })
                  void onBuy({
                    isin: fundIsin,
                    fundName: fund?.fundName ?? '',
                    amount: buyRecommendationFormattedAmount.value
                  })
                }
              }}
            />
          )
        }}
      />
      <RebalancePlanImportantNote importantNotes={sellPlanDetails?.importantNotes ?? null} />
    </div>
  )
}
